"use client";

import * as React from "react";
import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { ICON_REGISTRY, type IconName } from "../../components/ui/icon-registry";
import { Input } from "../../components/ui/input";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text } from "../../components/ui/text";
import type { Color } from "../../lib/colors";
import { WASH_HOVER, WASH_SELECTED } from "../../components/ui/box-classes";
import { cn } from "../../lib/utils";
import { ColorPalette } from "../color-swatch";
import type { Omittable } from "../../lib/props";

/** Icons suitable for entity picking (exclude UI-only icons like chevrons, x, etc.) */
const PICKER_ICON_NAMES: IconName[] = (
  Object.keys(ICON_REGISTRY) as IconName[]
).filter(
  (name) =>
    !name.startsWith("chevron") &&
    !name.startsWith("chevrons") &&
    !name.startsWith("arrow-") &&
    name !== "x" &&
    name !== "x-circle" &&
    name !== "minus" &&
    name !== "plus" &&
    name !== "check" &&
    name !== "loader" &&
    name !== "grip-vertical" &&
    name !== "more-horizontal" &&
    name !== "more-vertical" &&
    name !== "menu",
);

export type IconPickerSize = "sm" | "default";
export type IconPickerVariant = "soft" | "solid";

const TRIGGER_CLASSES: Record<IconPickerSize, string> = {
  sm: "h-8 w-8",
  default: "p-2",
};

export type IconPickerProps = Omittable<{
  icon: IconName;
  color: Color;
  onIconChange: (icon: IconName) => void;
  onColorChange: (color: Color) => void;
  /** Trigger button size. Defaults to 'default'. */
  size?: IconPickerSize;
  /** Trigger visual treatment.
   *  - 'soft' (default): tinted background + colored glyph (subtle).
   *  - 'solid': vibrant filled background + white glyph (app-tile look).
   */
  variant?: IconPickerVariant;
}>;

/**
 * Icon + color picker over the shared icon registry. The trigger shows the
 * current glyph on its tint; the popup carries the palette, a name filter and
 * the icon grid.
 *
 * lucide glyphs paint `currentColor`, so the solid variant's glyph is just
 * `text-c-foreground` — the foreground the `[data-color]` cascade already pairs with each
 * fill, black on the light hues and white on the saturated ones.
 */
export const IconPicker = React.memo(function IconPicker({
  icon,
  color,
  onIconChange,
  onColorChange,
  size = "default",
  variant = "soft",
}: IconPickerProps) {
  const [search, setSearch] = React.useState("");

  const filteredIcons = React.useMemo(() => {
    if (!search) return PICKER_ICON_NAMES;
    const lower = search.toLowerCase();
    return PICKER_ICON_NAMES.filter((name) => name.includes(lower));
  }, [search]);

  const solid = variant === "solid";

  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          "flex items-center justify-center rounded-md",
          TRIGGER_CLASSES[size],
          solid ? "bg-c" : "bg-c-soft",
        )}
        data-color={color}
        data-slot="icon-picker-trigger"
      >
        <Icon
          name={icon}
          size={size === "sm" ? "lg" : "xl"}
          className={solid ? "text-c-foreground" : "text-c"}
        />
      </PopoverTrigger>
      <PopoverPopup align="start" className="w-80 p-3" side="bottom">
        {/* Color swatches */}
        <Box paddingBottom="sm">
          <ColorPalette color={color} onColorChange={onColorChange} />
        </Box>

        {/* Search */}
        <Input
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search icons..."
          size="sm"
          value={search}
        />

        {/* Icon grid */}
        <Box direction="column" height={200} paddingTop="xs">
          <ScrollArea>
            {/* The popup is portalled, so the trigger's `data-color` does not
                reach it — the grid declares the tint for its own glyphs. */}
            <Box direction="row" gap="2xs" wrap data-color={color}>
              {filteredIcons.map((name) => (
                <Box align="center" justify="center" rounded="md"
                  as="button"
                  aria-label={name}
                  aria-pressed={name === icon}
                  className={cn(
                    "p-1.5",
                    // The grid renders inside this component's own
                    // PopoverPopup, so its surface is fixed, not inherited.
                    name === icon
                      ? WASH_SELECTED
                      : WASH_HOVER,
                  )}
                  key={name}
                  onClick={() => onIconChange(name)}
                  type="button"
                >
                  <Icon className="text-c" name={name} size="lg" />
                </Box>
              ))}
              {filteredIcons.length === 0 && (
                <Text className="p-2" size="sm" variant="muted">
                  No icons found
                </Text>
              )}
            </Box>
          </ScrollArea>
        </Box>
      </PopoverPopup>
    </Popover>
  );
});
